'use client'

import { useState, useMemo, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faSpinner,
  faExclamationTriangle,
  faRefresh,
  faSquare
} from '@fortawesome/free-solid-svg-icons'
import Image from 'next/image'
import { useStandings } from '@/hooks/useStandings'
import '@/app/bang-xep-hang-bong-da/bang-xep-hang.css'

// Available leagues
const AVAILABLE_LEAGUES = [
  { id: 39, name: 'Premier League', logo: 'https://media.api-sports.io/football/leagues/39.png' },
  { id: 140, name: 'La Liga', logo: 'https://media.api-sports.io/football/leagues/140.png' },
  { id: 135, name: 'Serie A', logo: 'https://media.api-sports.io/football/leagues/135.png' },
  { id: 78, name: 'Bundesliga', logo: 'https://media.api-sports.io/football/leagues/78.png' },
  { id: 61, name: 'Ligue 1', logo: 'https://media.api-sports.io/football/leagues/61.png' }
]

export default function BangXepHang() {
  const [activeLeague, setActiveLeague] = useState<number>(39) // Start with Premier League
  
  const leaguesRef = useRef<HTMLDivElement>(null)
  
  // Use the standings hook
  const { standings, loading, error, refetch } = useStandings({
    leagueId: activeLeague,
    autoRefresh: true,
    refreshInterval: 300000 // 5 minutes
  })
  
  // Handle wheel scroll for horizontal scrolling
  const handleWheelScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    if (target && e.deltaY !== 0 && target.scrollWidth > target.clientWidth) {
      e.preventDefault()
      target.scrollLeft += e.deltaY * 0.5
    }
  }

  // Generate league filters
  const leagues = useMemo(() => {
    return AVAILABLE_LEAGUES.map(league => ({
      id: league.id,
      name: league.name,
      logo: league.logo,
      count: standings?.id === league.id ? standings.teams.length : 0
    }))
  }, [standings])

  const handleLeagueChange = (leagueId: number) => {
    setActiveLeague(leagueId)
  }

  const getPositionColor = (rank: number, totalTeams: number) => {
    if (rank <= 4) return 'champions-league' // Top 4 - Champions League
    if (rank <= 6) return 'europa-league' // 5-6 - Europa League
    if (rank > totalTeams - 3) return 'relegation' // Bottom 3 - Relegation
    return ''
  }

  const getFormIcon = (result: string) => {
    switch (result) {
      case 'W': return <span className="form-w">W</span>
      case 'D': return <span className="form-d">D</span>
      case 'L': return <span className="form-l">L</span>
      default: return <span className="form-unknown">-</span>
    }
  }

  return (
    <div className="standings-results">
      {/* League Filters */}
      <div className="league-filters" ref={leaguesRef} onWheel={handleWheelScroll}>
        {leagues.map((league) => (
          <button
            key={league.id}
            className={`league-filters__button ${
              activeLeague === league.id ? 'league-filters__button--active' : ''
            }`}
            onClick={() => handleLeagueChange(league.id)}
          >
            {league.logo && (
              <Image
                src={league.logo}
                alt={league.name}
                width={20}
                height={20}
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}
            <span className="league-name">{league.name}</span>
            {league.count > 0 && <span>{league.count}</span>}
          </button>
        ))}
      </div>

      {/* Standings Container */}
      <div className="results-grid">
        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Đang tải bảng xếp hạng...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-state">
            <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
            <div>
              <p>Có lỗi xảy ra: {error}</p>
              <button className="retry-btn" onClick={refetch}>
                <FontAwesomeIcon icon={faRefresh} />
                Thử lại
              </button>
            </div>
          </div>
        )}

        {/* No Results */}
        {!loading && !error && !standings && (
          <div className="no-results">
            <FontAwesomeIcon icon={faSquare} size="2x" />
            <p>Không có bảng xếp hạng nào</p>
          </div>
        )}

        {/* Display standings */}
        {!loading && !error && standings && (
          <section className="results-section">
            <div className="league-schedule">
              <div className="league-header">
                <Image 
                  src={standings.logo} 
                  alt={standings.name}
                  width={32}
                  height={32}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
                <h2>{standings.name}</h2>
                <span className="match-count">Mùa giải {standings.season}</span>
              </div>

              {/* Table Format for Standings */}
              <div className="standings-table-container">
                {/* Table Header */}
                <div className="standings-table-header">
                  <div className="standings-col standings-col-rank">Hạng</div>
                  <div className="standings-col standings-col-team">Đội</div>
                  <div className="standings-col standings-col-played">Trận</div>
                  <div className="standings-col standings-col-won">Thắng</div>
                  <div className="standings-col standings-col-draw">Hòa</div>
                  <div className="standings-col standings-col-lost">Thua</div>
                  <div className="standings-col standings-col-gf">BT</div>
                  <div className="standings-col standings-col-ga">BB</div>
                  <div className="standings-col standings-col-gd">HS</div>
                  <div className="standings-col standings-col-points">Điểm</div>
                  <div className="standings-col standings-col-form">Phù</div>
                </div>

                {/* Table Body */}
                <div className="standings-table-body">
                  {standings.teams.map((team) => (
                    <div key={team.team.id} className={`standings-table-row ${getPositionColor(team.rank, standings.teams.length)}`}>
                      <div className="standings-col standings-col-rank">
                        <span className="rank-number" data-rank={team.rank}>
                          {team.rank}
                        </span>
                      </div>
                      
                      <div className="standings-col standings-col-team">
                        <Image 
                          src={team.team.logo}
                          alt={team.team.name}
                          width={20}
                          height={20}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                        <span className="standings-team-name">{team.team.name}</span>
                      </div>

                      <div className="standings-col standings-col-played">{team.played}</div>
                      <div className="standings-col standings-col-won">{team.win}</div>
                      <div className="standings-col standings-col-draw">{team.draw}</div>
                      <div className="standings-col standings-col-lost">{team.lose}</div>
                      <div className="standings-col standings-col-gf">{team.goalsFor}</div>
                      <div className="standings-col standings-col-ga">{team.goalsAgainst}</div>
                      <div className="standings-col standings-col-gd">
                        <span className={team.goalDifference >= 0 ? 'positive' : 'negative'}>
                          {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                        </span>
                      </div>
                      <div className="standings-col standings-col-points">
                        <span className="points-display">{team.points}</span>
                      </div>
                      <div className="standings-col standings-col-form">
                        <div className="form-display">
                          {team.form?.slice(-5).map((result, idx) => (
                            <span key={idx} className="form-item">
                              {getFormIcon(result)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  )
} 